import {
  useEffect,
  useId,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { LIVE_NOTICE_MS, type LiveArrival } from './useLiveArrivalNotice';

export const LIVE_NOTICE_EXIT_MS = 220;

/** Retain the outgoing pill just long enough to fade without affecting layout. */
export function LiveTokenNotice({
  arrival,
  gameSelected,
  onDismiss,
  onSwitchLive,
}: {
  arrival: LiveArrival | null;
  gameSelected: boolean;
  onDismiss: () => void;
  onSwitchLive: () => void;
}) {
  const [present, setPresent] = useState(arrival);
  useEffect(() => {
    if (arrival) {
      setPresent(arrival);
      return;
    }
    const timeout = window.setTimeout(
      () => setPresent(null),
      LIVE_NOTICE_EXIT_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [arrival]);
  if (!present) return null;
  return (
    <LiveNoticePill
      key={present.id + ':' + present.shownAt}
      arrival={present}
      closing={!arrival}
      gameSelected={gameSelected}
      onDismiss={onDismiss}
      onSwitchLive={onSwitchLive}
    />
  );
}

function LiveNoticePill({
  arrival,
  closing,
  gameSelected,
  onDismiss,
  onSwitchLive,
}: {
  arrival: LiveArrival;
  closing: boolean;
  gameSelected: boolean;
  onDismiss: () => void;
  onSwitchLive: () => void;
}) {
  const messageId = useId();
  const [elapsed] = useState(() => Math.max(0, Date.now() - arrival.shownAt));
  const onEscape = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onDismiss();
    }
  };
  return (
    <aside
      className="live-token-notice"
      aria-label="New Live token"
      aria-hidden={closing || undefined}
      inert={closing}
      style={
        {
          '--live-notice-ms': LIVE_NOTICE_MS + 'ms',
          '--live-notice-elapsed': -elapsed + 'ms',
          '--live-notice-exit-ms': LIVE_NOTICE_EXIT_MS + 'ms',
        } as CSSProperties
      }
    >
      <div className={'live-notice-pill' + (closing ? ' is-closing' : '')}>
        <p id={messageId} role="status" aria-live="polite" aria-atomic="true">
          A new token has appeared in
          <br />
          the live stream. Want to switch?
        </p>
        <button
          className="live-notice-close"
          type="button"
          aria-label="Dismiss Live token notice"
          onClick={onDismiss}
          onKeyDown={onEscape}
        >
          <img
            src="/images/mobile-calls-close.svg"
            width="18"
            height="18"
            alt=""
          />
        </button>
        <span className="live-notice-divider" aria-hidden="true">
          <img
            src="/images/live-notice-divider.svg"
            width="279"
            height="1"
            alt=""
          />
        </span>
        <button
          className="live-empty-switch live-notice-switch"
          type="button"
          role="switch"
          aria-checked={gameSelected}
          aria-label="Game Mode — switch to Live"
          aria-describedby={messageId}
          onClick={onSwitchLive}
          onKeyDown={onEscape}
        >
          <span className={gameSelected ? '' : 'is-active'}>live</span>
          <span className="live-empty-switch-track" aria-hidden="true">
            <img src="/images/rail-dot-run.svg" width="22" height="22" alt="" />
          </span>
          <span className={gameSelected ? 'is-active' : ''}>game</span>
        </button>
        <img
          className="live-notice-timer"
          src="/images/live-notice-timer.svg"
          width="343"
          height="58"
          alt=""
          aria-hidden="true"
        />
      </div>
    </aside>
  );
}
